/* eslint-disable @angular-eslint/prefer-standalone */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { VendorQuery } from 'src/app/store/vendor/vendor.query';
import {
  EventBusService,
  IStateData,
  LoggerDatadogService,
  UserAccount,
  VendorProfile,
  VendorState
} from 'sustainment-component';

import { CapabilityActions } from 'src/app/store/capability/capability.action';
import { DocumentTypeActions } from 'src/app/store/documenttype/documenttype.action';
import { ProjectsActions } from 'src/app/store/projects/projects.action';
import { ProjectsQuery } from 'src/app/store/projects/projects.query';
import { PublicOpportunitiesAction } from 'src/app/store/public-opportunities/public-opportunities.action';
import { PublicOpportunitiesQuery } from 'src/app/store/public-opportunities/public-opportunities.query';
import { StateActions } from 'src/app/store/state/state.action';
import { UserAccountQuery } from 'src/app/store/userAccount/user-account.query';
import {
  NavigationItem,
  ChatNewService,
  IdentityApi,
  UserRole,
  VendorAPI,
  UserProblem
} from 'sustainment-component';
import { AuthService } from 'src/app/services/auth.service';
import { VendorStore } from 'src/app/store/vendor/vendor.store';
import { UserAccountAction } from 'src/app/store/userAccount/user-account.action';
import { RegistrationSteps } from 'src/app/components/features/registration/registration.component';

@Component({
  selector: 'app-vendor-info',
  templateUrl: './vendor-info.component.html',
  styleUrls: ['./vendor-info.component.scss'],
  standalone: false
})
export class VendorInfoComponent implements OnInit, OnDestroy {
  /**
   * AVJ: Even if we aren't using these services in this component,
   * we still need to inject them to ensure they are initialized for the whole app.
   * This way we will not lose logging of errors, events coming in, etc.
   *
   * These variables can be renamed later if they end up getting explicitly used here.
   */
  readonly #_ = inject(LoggerDatadogService);
  readonly #__ = inject(EventBusService);

  public topNavItems: Array<NavigationItem> = [];
  public sideNavBuyersItems: Array<NavigationItem> = [];
  public sideNavSuppliersItems: Array<NavigationItem> = [];
  public sideNavOrgItems: Array<NavigationItem> = [];
  public sideNavItems: Array<NavigationItem> = [];
  public sortedSideNavItems: Array<NavigationItem> = [];
  public vendor$: Observable<VendorProfile>;
  public vendorInfo: VendorState;
  public stateList$: Observable<IStateData[]>;
  public profileDropdownItems: Array<NavigationItem> = [];
  public userNames$: Observable<string[]>;
  public companyName$: Observable<string>;

  public canCreateProject = false;
  public isSubscribed = false;
  public menuIsClosed: boolean;
  public max_projects = 0;
  public rolesSelected: boolean;
  public roles: { id: number; text: string }[] = [];
  public problems: { id: number; text: string }[] = [];

  public steps = RegistrationSteps;
  public currentStep = RegistrationSteps.CompanyTypeRegistration;
  public problemSelected: boolean;

  public selectedProblems = signal<UserProblem[]>([]);

  public userRoles = signal<UserRole[]>([]);
  public userHasRolesSet = signal<boolean>(false);

  private _subscriptions: Subscription[] = [];
  public user: UserAccount;
  public savingRoles: boolean;
  public constructor(
    private vendorQuery: VendorQuery,
    private userAccountQuery: UserAccountQuery,
    private _router: Router,
    private capabilityActions: CapabilityActions,
    private projectsQuery: ProjectsQuery,
    private projectsActions: ProjectsActions,
    private opportunitiesAction: PublicOpportunitiesAction,
    private opportunitiesQuery: PublicOpportunitiesQuery,
    private _stateActions: StateActions,
    private documentTypeAction: DocumentTypeActions,
    private authService: AuthService,
    private chatService: ChatNewService,
    private vendorStore: VendorStore,
    private identityApi: IdentityApi,
    private userAccountAction: UserAccountAction,
    private vendorAPI: VendorAPI
  ) {
    this.capabilityActions.getCapabilityData();
    this.projectsActions.getPendingCount();
    this.opportunitiesAction.getPendingRequestCount();
    this.chatService.startChat();

    this.userAccountQuery.userAccount$.subscribe((user) => (this.user = user));
    this._stateActions.getStateData();
    this.documentTypeAction.getDocumentTypesData();
  }

  public logout(): void {
    this.authService.logout();
  }

  public onMenuClosed(value: boolean): void {
    this.menuIsClosed = value;
  }

  public ngOnInit(): void {
    const userRoles = this.userAccountQuery.getUserRoles();

    this.userHasRolesSet.set((userRoles && userRoles.length > 0) ?? false);

    this.vendor$ = this.vendorQuery
      .select((v) => v.vendor)
      .pipe(first((v) => !!v));

    this.vendorQuery
      .select((e) => e.vendor.alias)
      .subscribe((alias) => {
        if (!alias) return;

        const item = this.profileDropdownItems.find(
          (e) => e.label === 'Company profile'
        );

        if (!item) return;

        item.url = `profile/${alias}`;
      });

    this.buildTopNav();
    this.buildSideNav();

    this.userNames$ = this.userAccountQuery.userAccount$.pipe(
      map((res) => {
        return [res?.firstName, res?.lastName].filter((x) => !!x);
      })
    );

    this.companyName$ = this.userAccountQuery.userAccount$.pipe(
      map((res) => res?.organizations[0].name)
    );

    this.identityApi.getUserRoles().subscribe((roles) => {
      this.userRoles.set(roles);
    });

    this.profileDropdownItems.push({
      label: 'Company profile',
      icon: 'pi pi-building',
      url: `profile/${
        this.vendorQuery.alias || this.userAccountQuery.sustainmentId
      }`
    });

    this.profileDropdownItems.push({
      label: 'Settings',
      icon: 'pi pi-cog',
      url: 'settings/'
    });
  }

  public ngOnDestroy(): void {
    this._subscriptions?.forEach((sub) => sub.unsubscribe());
  }

  public goToProblemSelection(): void {
    this.selectedProblems.set(
      this.userRoles()
        .filter((x) => this.roles.map((e) => e.id).includes(x.userRoleId))
        .reduce((acc: UserProblem[], x) => {
          x.problems.forEach((problem) => {
            if (!acc.some((p) => p.userProblemId === problem.userProblemId)) {
              acc.push(problem);
            }
          });
          return acc;
        }, [])
        .sort((a, b) => a.userProblemId - b.userProblemId)
    );

    if (this.selectedProblems().length === 0) {
      this.saveRoles();
      return;
    }

    this.currentStep = RegistrationSteps.UserProblems;
  }

  public goBack(): void {
    this.currentStep = RegistrationSteps.CompanyTypeRegistration;
  }

  public saveRoles(): void {
    this.rolesSelected = false;
    this.savingRoles = true;
    this.identityApi
      .updateUserRoles(
        this.userAccountQuery.sustainmentId,
        this.roles.map((e) => e.id),
        true
      )
      .subscribe(() => {
        this.userAccountAction.updateUserRoles(this.roles.map((e) => e.text));
        this.userAccountAction.updateUserProblems(this.problems);
        this.userAccountAction.showedWelcome();
        this.vendorAPI
          .updateSupplierType({
            isOther: this.roles.map((e) => e.id).includes(6),
            isSupplier: this.roles.map((e) => e.id).includes(5),
            isBuyer: this.roles.some((x) => x.id <= 4)
          })
          .subscribe();
        this.userHasRolesSet.set(true);
      });
  }

  private buildTopNav(): void {
    this.topNavItems = [];

    this.vendor$.subscribe((vendor) => {
      if (vendor && !vendor.isInternational) {
        this.topNavItems.push({
          title: 'Resources',
          url: '/resources',
          icon: 'ti ti-layers-linked',
          position: 1
        });
      }
    });

    this.topNavItems.push({
      title: 'Connections',
      url: './connections',
      icon: 'ti ti-social',
      position: 2
    });

    this.topNavItems.push({
      title: 'Help',
      url: 'https://knowledgebase.sustainment.tech/knowledge',
      icon: 'ti ti-help',
      isExternalLink: true,
      position: 3
    });
  }

  private createProject(): void {
    if (!this.canCreateProject) {
      return;
    }
    this._router.navigateByUrl('projects/creation');
  }

  private buildSideNav(): void {
    this.sideNavItems.push({
      title: 'Home',
      url: './home',
      icon: 'dashboard',
      position: 0
    });

    if (this.userAccountQuery.hasNetworkTabAccess) {
      this.sideNavItems.push({
        title: 'Network',
        url: './network',
        icon: 'network',
        position: 4
      });
    }

    /* Buyers panel */

    this.sideNavBuyersItems.push({
      title: 'Sourcing',
      icon: 'list-search',
      url: './projects/sourcing',
      position: 5
    });

    this.sideNavBuyersItems.push({
      title: 'Procurement',
      icon: 'shopping-cart',
      url: './projects/procurement',
      position: 6
    });

    this.sideNavBuyersItems.push({
      title: 'Suppliers',
      url: 'suppliers',
      icon: 'suppliers',
      position: 7
    });

    this.sideNavBuyersItems.push({
      title: 'Parts library',
      url: './parts',
      icon: 'parts-library',
      position: 8
    });

    this.sideNavBuyersItems.push({
      title: 'Analytics',
      url: './analytics',
      icon: 'analytics',
      position: 8
    });

    /* Suppliers panel */

    this.sideNavSuppliersItems.push({
      title: 'Opportunities',
      url: './opportunities',
      icon: 'bulb',
      position: 9
    });

    this.sideNavSuppliersItems.push({
      title: 'Orders',
      url: 'orders',
      icon: 'receipt',
      position: 11
    });

    this.sideNavSuppliersItems.push({
      title: 'Customers',
      url: 'customers',
      icon: 'customers',
      position: 11
    });

    this.projectsActions.getProjectsData();

    this._subscriptions.push(
      this.projectsQuery
        .select((x) => x.projects)
        .pipe(filter((projects) => projects !== undefined))
        .pipe(
          tap((projects) => {
            const label = this.userAccountQuery.isAdmin
              ? 'You have reached the maximum number of Active projects for your Community account. \n Upgrade now to increase your maximum number of projects.'
              : '';
            const icon = 'pi pi-plus create-project';
            const iconDisabled = 'pi pi-plus create-project disabled';

            this.max_projects = this.userAccountQuery.max_projects;

            const activeStatus = ['Open', 'Awarded'];
            const activeProjects = projects.filter((x) =>
              activeStatus.includes(x.status)
            );
            if (activeProjects.length >= this.max_projects)
              this.canCreateProject = false;
            else this.canCreateProject = true;

            const newProject = this.sideNavItems.findIndex(
              (x) => x.title === 'Create'
            );

            if (newProject > -1) {
              if (this.canCreateProject) {
                this.sideNavItems[newProject].label = '';
                this.sideNavItems[newProject].icon = icon;
              } else {
                this.sideNavItems[newProject].label = label;
                this.sideNavItems[newProject].icon = iconDisabled;
              }
            } else {
              if (this.canCreateProject) {
                this.sideNavItems.push({
                  title: 'Create',
                  action: () => this.createProject(),
                  icon: icon,
                  position: 10
                });
              } else {
                this.sideNavItems.push({
                  title: 'Create',
                  icon: iconDisabled,
                  position: 10
                });
              }
            }

            this.sortedSideNavItems = [
              ...this.sideNavItems,
              ...this.sideNavBuyersItems,
              ...this.sideNavSuppliersItems,
              ...this.sideNavOrgItems
            ];
          })
        )
        .subscribe()
    );
  }
}
